/**
 * MissionControl — Vue détaillée d'une mission
 *
 * Layout : attempts bar (top) + trace feed (left) + side panel (right)
 * Side panel : crew status, satisfaction gauge, journal de bord
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import type { Mission, CrewMember } from "@synapse/shared";
import { useMissionTraces } from "../hooks/useMissionTraces";
import MissionTrace from "../components/MissionTrace";
import { showToast } from "../components/Toast";

export default function MissionControl() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [mission, setMission] = useState<Mission | null>(null);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const { traces, connected } = useMissionTraces(id ?? null);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const m = await api.missions.get(id);
      setMission(m);

      // Load crew
      const allCrew = await api.crew.list();
      const missionCrew = m.crewIds?.length
        ? allCrew.filter((c) => m.crewIds.includes(c.id))
        : [];
      setCrew(missionCrew);
    } catch {
      // mission not found
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
    // Refresh mission status every 5s when running
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleStart = async () => {
    if (!id) return;
    setStarting(true);
    try {
      await api.missions.start(id);
      showToast("info", "Mission démarrée", "La mission a été mise en file d'exécution.");
      await loadData();
    } catch (err) {
      // CMD-01: surfacer la raison du refus (ex. LLM_NOT_CONFIGURED) au lieu de l'avaler.
      const msg = err instanceof Error ? err.message : "Échec du démarrage";
      showToast("error", "Démarrage impossible", msg);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="page active" style={{ display: "grid", gap: "14px" }}>
        <div className="no-results">Chargement de la mission…</div>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="page active" style={{ display: "grid", gap: "14px" }}>
        <div className="no-results">Mission introuvable.</div>
        <button className="btn" onClick={() => navigate("/")}>← Retour au dashboard</button>
      </div>
    );
  }

  // Compute attempt circles
  const attempts = Array.from({ length: mission.maxAttempts }, (_, i) => {
    const num = i + 1;
    const tracesForAttempt = traces.filter((t) => t.attemptNumber === num);
    const hasError = tracesForAttempt.some((t) => t.status === "error");
    const hasSuccess = tracesForAttempt.some((t) => t.status === "success");
    const isCurrent = num === mission.attempt;
    if (isCurrent && !hasError && !hasSuccess) return { num, state: "current" as const };
    if (hasSuccess && !hasError) return { num, state: "pass" as const };
    if (hasError) return { num, state: "fail" as const };
    return { num, state: "pending" as const };
  });

  // Satisfaction gauge
  const satisfaction = mission.satisfaction ?? 0;
  const minSatisfaction = mission.minSatisfaction ?? 90;

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="eyebrow" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            ← Dashboard
          </div>
          <h1>{mission.title}</h1>
          <p>
            Mission · statut: <strong>{mission.status}</strong> · mode:{" "}
            <span className={`mode-pill ${mission.controlMode}`}>{mission.controlMode}</span>
          </p>
        </div>
        <div className="header-actions">
          {mission.status !== "done" && mission.status !== "validation" && (
            <button className="btn primary" onClick={handleStart} disabled={starting}>
              {starting ? "⏳ Démarrage…" : "▶ Démarrer la mission"}
            </button>
          )}
        </div>
      </div>

      {/* ── Attempts Bar ───────────────────────────────────────── */}
      <div className="attempts-bar">
        {attempts.map((a, i) => (
          <div key={a.num} className="attempt-block">
            <div className={`attempt-circle ${a.state}`}>
              {a.state === "pass" ? "✓" : a.state === "fail" ? "✗" : a.num}
            </div>
            <div className="attempt-label">
              {a.state === "current" ? "En cours" : a.state === "pass" ? "Réussi" : a.state === "fail" ? "Échec" : `Tentative ${a.num}`}
            </div>
            {i < attempts.length - 1 && <div className="attempt-connector" />}
          </div>
        ))}
      </div>

      {/* ── Trace + Side Panel ─────────────────────────────────── */}
      <div className="trace-layout">
        {/* Left: Trace Feed */}
        <MissionTrace traces={traces} connected={connected} />

        {/* Right: Side Panel */}
        <div className="side-panel">
          {/* Mission Info */}
          <div className="side-card">
            <strong>🎯 Mission</strong>
            <div className="side-row"><span>Statut</span><strong>{mission.status}</strong></div>
            <div className="side-row"><span>Tentative</span><strong>{mission.attempt}/{mission.maxAttempts}</strong></div>
            <div className="side-row"><span>Objectif réussite</span><strong>{mission.targetAttempts} essais</strong></div>
            {mission.description && (
              <div style={{ paddingTop: "8px", fontSize: "13px", color: "var(--muted)", lineHeight: 1.4 }}>
                {mission.description}
              </div>
            )}
          </div>

          {/* Satisfaction Gauge */}
          <div className="side-card">
            <strong>📊 Satisfaction</strong>
            <div className="gauge">
              <div className="gauge-bar">
                <div
                  className="gauge-fill"
                  style={{ width: `${satisfaction}%` }}
                />
                {minSatisfaction > 0 && (
                  <div
                    className="gauge-target"
                    style={{ left: `${minSatisfaction}%` }}
                  />
                )}
              </div>
              <div className="gauge-labels">
                <span>0%</span>
                <span style={{ color: "var(--purple)", fontWeight: 600 }}>{satisfaction}%</span>
                <span>100%</span>
              </div>
            </div>
            <div className="side-row"><span>Minimum requis</span><strong>{minSatisfaction}%</strong></div>
          </div>

          {/* Crew Status */}
          <div className="side-card">
            <strong>👥 Équipage</strong>
            {crew.length > 0 ? (
              <div className="crew-status-list">
                {crew.map((member) => (
                  <div key={member.id} className="crew-status">
                    <div className="crew-status-icon" style={{ background: member.avatarColor || "var(--purple)" }}>
                      {member.avatar || "🤖"}
                    </div>
                    <div className="crew-status-info">
                      <div className="crew-status-name">{member.name}</div>
                      <div className="crew-status-detail">{member.role} · {member.llmModel}</div>
                    </div>
                    <div className={`crew-status-light ${member.active ? "idle" : ""}`} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: "13px", color: "var(--muted)" }}>
                Aucun équipage assigné. La mission utilisera l'orchestrateur par défaut.
              </div>
            )}
          </div>

          {/* Skills */}
          {mission.skills.length > 0 && (
            <div className="side-card">
              <strong>🛠 Compétences</strong>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {mission.skills.map((s) => (
                  <span key={s} className="skill-chip">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Journal de bord */}
          <div className="side-card">
            <strong>📓 Journal de bord</strong>
            <div className="journal-entry">
              <div className="journal-author">Système</div>
              <div className="journal-msg">Mission créée le {new Date(mission.createdAt).toLocaleString("fr-FR")}</div>
              <div className="journal-time">{new Date(mission.createdAt).toLocaleTimeString("fr-FR")}</div>
            </div>
            <div className="journal-input">
              <input placeholder="Ajouter une note…" disabled />
              <button disabled>Envoyer</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
